/**
 * Chapter 1 — the day-sheet becomes the Board: sheet rows slide in, each
 * becomes a Board row (chip + the engine's own drops sentence), then every
 * sheet column lights with the screen that replaced it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowRight } from 'lucide-react';

import { describeDrops } from '@/features/drop-generator/utils/drops/describe-drops';
import { ContainerChip } from '@/features/drop-generator/components/shared/ContainerChip';
import { DEMO_PLAN, DEMO_SHEET, SHEET_COLUMNS } from '../../fixtures';
import { Enter, Node } from '../../motion/Node';
import { Illustration } from '../../motion/Illustration';

const CYCLE = 14;
const ROW_TICK = 0.8;

/** The Board's own sentence for a modelled container, else the sheet's. */
function dropsLine(tag: string, amounts: string, every: string): string {
  const c = DEMO_PLAN.containers.find((x) => x.name === tag);
  return c ? describeDrops(c.slots, c.defaultAmount, c.intervalMin) : `${amounts} · every ${every}`;
}

export const SheetIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE}>
    <div className="grid gap-4 text-xs md:grid-cols-[1fr_auto_1fr]">
      <div className="flex flex-col gap-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Your sheet</p>
        {DEMO_SHEET.map((row, i) => (
          <Enter key={row.tag} at={i} tickSeconds={ROW_TICK}>
            <div className={`grid grid-cols-[1fr_auto] items-center gap-2 rounded border border-border/60 bg-background px-2 py-1 font-mono ${row.enabled ? '' : 'opacity-50'}`}>
              <span className="truncate font-semibold">{row.tag}</span>
              <span className="text-muted-foreground">{row.start} · {row.every}</span>
            </div>
          </Enter>
        ))}
      </div>
      <div className="hidden items-center md:flex">
        <ArrowRight className="h-4 w-4 text-muted-foreground/60" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">The Board</p>
        {DEMO_SHEET.map((row, i) => (
          <Enter key={row.tag} at={i + 1} tickSeconds={ROW_TICK}>
            <div className={`flex items-center gap-2 rounded border border-border/60 bg-background px-2 py-1 ${row.enabled ? '' : 'opacity-50'}`}>
              <ContainerChip name={row.tag} color={row.color} />
              <span className="min-w-0 truncate font-mono text-muted-foreground">
                {dropsLine(row.tag, row.amounts, row.every)}
              </span>
            </div>
          </Enter>
        ))}
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {SHEET_COLUMNS.map((col, i) => (
        <Node key={col.key} at={DEMO_SHEET.length + 1 + i * 0.6} tickSeconds={ROW_TICK}>
          <span className="font-mono font-semibold">{col.label}</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground/60" />
          <span className="text-muted-foreground">{col.home}</span>
        </Node>
      ))}
    </div>
  </Illustration>
);
